import { inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { startWith } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { WHTeamDataService, WHTeamDOM } from '@workheld/workheld-shared-lib';

// TODO: 18.7 remove old uiService and replace it in all components
// no time before release
type UIStoreState = {
  currentLocale: string;
  teams: Map<string, WHTeamDOM>;
  teamsLoading: boolean;
  isLargeScreen: boolean;
  isXLScreen: boolean;
  isSmallScreen: boolean;
  isFirefoxBrowser: boolean;
  isTouchDevice: boolean;
};

const initialState: UIStoreState = {
  currentLocale: 'en',
  teams: new Map<string, WHTeamDOM>(),
  teamsLoading: true,
  isLargeScreen: false,
  isXLScreen: false,
  isSmallScreen: false,
  isFirefoxBrowser: false,
  isTouchDevice: false,
};

export const UIStore = signalStore(
  { providedIn: 'root' },

  withState(initialState),
  withMethods((store) => ({
    getTeamById(id: string): WHTeamDOM | undefined {
      return store.teams().get(id);
    },
  })),
  withHooks({
    onInit(
      store,
      breakpointObserver = inject(BreakpointObserver),
      teamDataService = inject(WHTeamDataService),
      translateService = inject(TranslateService),
    ) {
      const isFirefoxBrowser = navigator.userAgent
        .toLowerCase()
        .includes('firefox');

      const isTouchDevice =
        'ontouchstart' in window ||
        navigator.maxTouchPoints > 0 ||
        window.matchMedia('(pointer: coarse)').matches;

      patchState(store, { isFirefoxBrowser, isTouchDevice });

      breakpointObserver
        .observe([
          Breakpoints.Small,
          Breakpoints.XSmall,
          Breakpoints.Handset,
          Breakpoints.Large,
          Breakpoints.XLarge,
        ])
        .subscribe((result) => {
          const isLargeScreen = Object.values(result.breakpoints).some(
            (value) => value === true && result.breakpoints[Breakpoints.Large],
          );
          const isXLScreen = Object.values(result.breakpoints).some(
            (value) => value === true && result.breakpoints[Breakpoints.XLarge],
          );

          const isSmallScreen = Object.values(result.breakpoints).some(
            (value) =>
              value === true &&
              (result.breakpoints[Breakpoints.XSmall] ||
                result.breakpoints[Breakpoints.Small] ||
                result.breakpoints[Breakpoints.Medium] ||
                result.breakpoints[Breakpoints.Handset]),
          );

          patchState(store, { isLargeScreen, isXLScreen, isSmallScreen });
        });

      teamDataService.teams$.subscribe((teams: WHTeamDOM[]) => {
        const teamMap = new Map<string, WHTeamDOM>();
        teams.forEach((team: WHTeamDOM) => {
          teamMap.set(team.id, team);
        });

        patchState(store, { teams: teamMap, teamsLoading: false });
      });

      translateService.onLangChange.pipe(startWith('en')).subscribe(() => {
        patchState(store, { currentLocale: translateService.currentLang });
      });
    },
  }),
);
